import { useMemo, useRef, useState } from "react";
import { isSinclairEmail } from "@context/shared";
import { useStudent } from "../../state/StudentProvider";
import { deriveStudentMessages } from "../../state/studentMessages";
import { BottomStatusBar } from "./BottomStatusBar";
import { EditorToolbar } from "./EditorToolbar";
import { LiveWritingSummary } from "./LiveWritingSummary";
import { PromptPanel } from "./PromptPanel";
import { StudentToolbox } from "./StudentToolbox";
import { WritingEditor, type WritingEditorHandle } from "./WritingEditor";

const PROFESSOR_EMPTY_ERROR = "Enter your professor's Sinclair email.";
const PROFESSOR_INVALID_ERROR = "Enter a valid Sinclair email address (name@sinclair.edu).";
const STUDENT_EMPTY_ERROR = "Enter your Sinclair email.";
const STUDENT_INVALID_ERROR = "Enter a valid Sinclair email address (name@sinclair.edu).";

function professorEmailErrorMessage(value: string): string | null {
  if (value.trim() === "") return PROFESSOR_EMPTY_ERROR;
  if (!isSinclairEmail(value)) return PROFESSOR_INVALID_ERROR;
  return null;
}

function studentEmailErrorMessage(value: string): string | null {
  if (value.trim() === "") return STUDENT_EMPTY_ERROR;
  if (!isSinclairEmail(value)) return STUDENT_INVALID_ERROR;
  return null;
}

export function StudentView({ active }: { active: boolean }) {
  const { state, review, actions } = useStudent();
  const [focusMode, setFocusMode] = useState(false);
  const [professorEmailError, setProfessorEmailError] = useState<string | null>(null);
  const [studentEmailError, setStudentEmailError] = useState<string | null>(null);

  const editorRef = useRef<WritingEditorHandle>(null);
  const professorEmailRef = useRef<HTMLInputElement>(null);

  const messages = useMemo(
    () => deriveStudentMessages(state.draft, review, state.guidance),
    [state.draft, review, state.guidance],
  );

  const handleProfessorEmailChange = (value: string) => {
    actions.setProfessorEmail(value);
    // While an error is showing, re-validate live and clear it as soon as the
    // value becomes valid; a clean field stays quiet until blur or submit.
    setProfessorEmailError((current) => (current ? professorEmailErrorMessage(value) : null));
  };

  const handleStudentEmailChange = (value: string) => {
    actions.setStudentEmail(value);
    setStudentEmailError((current) => (current ? studentEmailErrorMessage(value) : null));
  };

  const handleProfessorEmailBlur = (value: string) => {
    setProfessorEmailError(professorEmailErrorMessage(value));
  };

  const handleStudentEmailBlur = (value: string) => {
    setStudentEmailError(studentEmailErrorMessage(value));
  };

  const handleStartAssignment = async () => {
    setProfessorEmailError(professorEmailErrorMessage(state.draft.professorEmail));
    if (!isSinclairEmail(state.draft.professorEmail)) {
      // Mirrors the prototype: refuse locally and send focus to the field.
      actions.setGuidance("Please enter your professor's Sinclair email (name@sinclair.edu).");
      professorEmailRef.current?.focus();
      return;
    }
    await actions.startAssignment();
    editorRef.current?.focus();
  };

  const handleSubmit = () => {
    setProfessorEmailError(professorEmailErrorMessage(state.draft.professorEmail));
    setStudentEmailError(studentEmailErrorMessage(state.draft.studentEmail));
    void actions.submit();
  };

  const canSubmit =
    state.draft.trackingActive && state.draft.text.trim().length > 0;

  const submitDisabledReason = !state.draft.trackingActive
    ? "Start the assignment before submitting so the writing session can be recorded."
    : state.draft.text.trim().length === 0
      ? "Write something in the editor before submitting."
      : null;

  // Until the workspace has loaded there is nothing authoritative to edit, and
  // anything typed would be dropped by the first sync. Rendering a placeholder
  // instead of a live-but-inert editor removes that whole class of lost input.
  if (state.status !== "ready") {
    return (
      <section
        className={`view-panel ${active ? "active" : ""}`.trim()}
        id="student-view"
        aria-busy="true"
      >
        <div className="assignment-header">
          <div className="assignment-header-bar">
            <h2>Opening your writing space</h2>
          </div>
          <div className="assignment-header-body">
            <p className="assignment-copy">
              Connecting to ConText. Your saved drafts and submissions will appear in a moment.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`view-panel ${active ? "active" : ""} ${focusMode ? "focus-mode" : ""}`.trim()}
      id="student-view"
    >
      <StudentToolbox
        professorEmail={state.draft.professorEmail}
        studentEmail={state.draft.studentEmail}
        saveStatus={state.saveStatus}
        trackingActive={state.draft.trackingActive}
        sessions={state.sessions}
        selectedSessionId={state.selectedSessionId}
        professorEmailRef={professorEmailRef}
        professorEmailError={professorEmailError}
        studentEmailError={studentEmailError}
        onProfessorEmailChange={handleProfessorEmailChange}
        onStudentEmailChange={handleStudentEmailChange}
        onProfessorEmailBlur={handleProfessorEmailBlur}
        onStudentEmailBlur={handleStudentEmailBlur}
        onStartAssignment={handleStartAssignment}
        onSubmit={handleSubmit}
        submitDisabled={!canSubmit}
        submitDisabledReason={submitDisabledReason}
        onSaveDraft={() => void actions.saveDraft()}
        onLoadSession={(id) => void actions.loadSession(id)}
        onDeleteSession={(id) => actions.deleteSession(id)}
      />

      <div className="student-grid">
        <div className="student-main-column">
          <section className={`editor-card ${state.draft.trackingActive ? "" : "editor-locked"}`.trim()}>
            <PromptPanel
              value={state.referencePrompt}
              onChange={actions.setReferencePrompt}
              messages={messages}
            />

            <div className="editor-header">
              <div>
                <p className="editor-label">Assignment instructions</p>
                <h3 id="assignment-instructions-heading">
                  Add assignment instructions in the prompt area above if you want them visible
                  while writing.
                </h3>
              </div>
            </div>

            <EditorToolbar
              trackingActive={state.draft.trackingActive}
              focusMode={focusMode}
              onCommand={(command, value) => editorRef.current?.applyCommand(command, value)}
              onToggleFocusMode={() => setFocusMode((value) => !value)}
            />

            <div className="editor-stage">
              <div className={`editor-lock ${state.draft.trackingActive ? "hidden" : ""}`.trim()}>
                <h4>Tracking is off until you are ready.</h4>
                <p>
                  You can review a saved draft or paste your assignment prompt first. Choose “Start
                  This Assignment” when you are ready for the writing timer and tracking to begin.
                </p>
              </div>
              <WritingEditor
                ref={editorRef}
                html={state.draft.html}
                contentVersion={state.contentVersion}
                trackingActive={state.draft.trackingActive}
                onMutation={actions.recordMutation}
                onKeydown={actions.recordKeydown}
                onPaste={actions.recordPaste}
                onFocus={actions.recordFocus}
                onBlur={actions.recordBlur}
              />
            </div>

            <LiveWritingSummary metrics={state.draft.metrics} messages={messages} />

            <BottomStatusBar reviewNotes={messages.reviewNotes} />
          </section>
        </div>
      </div>
    </section>
  );
}
